import { createSelector } from 'reselect';

import { ChainStoreReportsFavorite } from '../../domain/chain/types';
import { ChainReportsFormConditions } from '../../domain/chainReportsFormConditions';
import {
  DataChainStoreKi,
  DataChainStoreKiParams,
} from '../../domain/dataChainStoreKi';
import { DkSisSearchParams } from '../../domain/dkSis';
import { Option, OrderType } from '../../domain/schemas';
import { ShuOption } from '../../domain/shu';

import { RootState } from '../../store';
import { getOrderedTableData } from '../../utils/orderedCell';
import { ReportsDateUnit } from '../../utils/reportsDateRange';
import { chainStoreReportsSearchConditionSelector } from '../ui/chainStoreReportsSetting';

/**
 * Action Types
 */

const FETCH_DATA_CHAIN_STORE_KI = 'FETCH_DATA_CHAIN_STORE_KI' as const;
const FETCH_DATA_CHAIN_STORE_KI_REQUEST =
  'FETCH_DATA_CHAIN_STORE_KI_REQUEST' as const;
const FETCH_DATA_CHAIN_STORE_KI_SUCCESS =
  'FETCH_DATA_CHAIN_STORE_KI_SUCCESS' as const;
const INIT_DATA_CHAIN_STORE_KI = 'INIT_DATA_CHAIN_STORE_KI' as const;
const SEARCH_DATA_CHAIN_STORE_KI = 'SEARCH_DATA_CHAIN_STORE_KI' as const;
const SEARCH_DATA_CHAIN_STORE_KI_RESET =
  'SEARCH_DATA_CHAIN_STORE_KI_RESET' as const;
const SEARCH_DATA_CHAIN_STORE_KI_FIELD_TYPE =
  'SEARCH_DATA_CHAIN_STORE_KI_FIELD_TYPE' as const;
const SEARCH_DATA_CHAIN_STORE_KI_SORT =
  'SEARCH_DATA_CHAIN_STORE_KI_SORT' as const;
const CLEAR_DATA_CHAIN_STORE_KI = 'CLEAR_DATA_CHAIN_STORE_KI' as const;
const CLEAR_ALL_DATA_CHAIN_STORE_KI = 'CLEAR_ALL_DATA_CHAIN_STORE_KI' as const;
const SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER =
  'SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER' as const;
const SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER_ALL =
  'SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER_ALL' as const;
const CHANGE_DATA_CHAIN_STORE_KI_COLUMNS_ORDER =
  'CHANGE_DATA_CHAIN_STORE_KI_COLUMNS_ORDER' as const;
const SEARCH_DATA_CHAIN_STORE_KI_DKSIS_ON =
  'SEARCH_DATA_CHAIN_STORE_KI_DKSIS_ON' as const;
const SEARCH_DATA_CHAIN_STORE_KI_DKSIS_OFF =
  'SEARCH_DATA_CHAIN_STORE_KI_DKSIS_OFF' as const;
const SEARCH_DATA_CHAIN_STORE_KI_DKSIS =
  'SEARCH_DATA_CHAIN_STORE_KI_DKSIS' as const;
const RENEW_DATA_CHAIN_STORE_KI = 'RENEW_DATA_CHAIN_STORE_KI' as const;
const SEARCH_DATA_CHAIN_STORE_KI_FAVORITE =
  'SEARCH_DATA_CHAIN_STORE_KI_FAVORITE' as const;

export const DataChainStoreKiActionTypes = {
  FETCH_DATA_CHAIN_STORE_KI,
  FETCH_DATA_CHAIN_STORE_KI_REQUEST,
  FETCH_DATA_CHAIN_STORE_KI_SUCCESS,
  INIT_DATA_CHAIN_STORE_KI,
  SEARCH_DATA_CHAIN_STORE_KI,
  SEARCH_DATA_CHAIN_STORE_KI_RESET,
  SEARCH_DATA_CHAIN_STORE_KI_FAVORITE,
  SEARCH_DATA_CHAIN_STORE_KI_FIELD_TYPE,
  SEARCH_DATA_CHAIN_STORE_KI_SORT,
  CLEAR_DATA_CHAIN_STORE_KI,
  CLEAR_ALL_DATA_CHAIN_STORE_KI,
  SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER,
  SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER_ALL,
  CHANGE_DATA_CHAIN_STORE_KI_COLUMNS_ORDER,
  SEARCH_DATA_CHAIN_STORE_KI_DKSIS_ON,
  SEARCH_DATA_CHAIN_STORE_KI_DKSIS_OFF,
  SEARCH_DATA_CHAIN_STORE_KI_DKSIS,
  RENEW_DATA_CHAIN_STORE_KI,
};

/**
 * Action Creators
 */

/**
 * 種別毎の新台/メイン機種を取得する
 * @param shu 種別
 * @param params 検索条件
 */
function fetchDataChainStoreKiAction(
  shuCode: string,
  params: DataChainStoreKiParams,
  dateUnit: ReportsDateUnit | undefined
) {
  return {
    type: FETCH_DATA_CHAIN_STORE_KI,
    payload: { shuCode, params, dateUnit },
  };
}

/**
 * APIにリクエスト前に呼ばれるもの
 * @param shuCode 種別コード
 */
function fetchDataChainStoreKiRequestAction(shuCode: string) {
  return {
    type: FETCH_DATA_CHAIN_STORE_KI_REQUEST,
    payload: { shuCode },
  };
}

/**
 * データ取得成功時新台/メイン機種データを更新する
 * @param shuCode 種別コード
 * @param shu 新台/メイン機種データ
 */
function fetchDataChainStoreKiSuccessAction(
  shuCode: string,
  shu: DataChainStoreKi
) {
  return {
    type: FETCH_DATA_CHAIN_STORE_KI_SUCCESS,
    payload: { shuCode, shu },
  };
}

/**
 * 指定した種別グループ・種別のデータを初回取得する
 * @param shu 種別データ（種別グループ・種別）
 */
function initDataChainStoreKiAction(shu: ShuOption) {
  return {
    type: INIT_DATA_CHAIN_STORE_KI,
    payload: { shu },
  };
}

/**
 * 現在表示されている新台/メイン機種を指定した検索条件で再取得する（検索用）
 * @param params 検索フォームの検索条件
 */
function searchDataChainStoreKiAction(params: ChainReportsFormConditions) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_KI,
    payload: { params },
  };
}

/**
 * 現在表示されている新台/メイン機種を初回の検索条件で再取得する（リセット用）
 */
function searchDataChainStoreKiResetAction() {
  return {
    type: SEARCH_DATA_CHAIN_STORE_KI_RESET,
  };
}

/**
 * 新台/メイン機種を取得する（お気に入り用）
 */
function searchDataChainStoreKiFavoriteAction(
  favorite: ChainStoreReportsFavorite
) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_KI_FAVORITE,
    payload: { favorite },
  };
}

/**
 * 指定した表示項目に絞って新台/メイン機種テーブルデータを再取得する
 * @param shu 種別データ（種別グループ・種別）
 * @param fields 表示項目
 */
function searchDataChainStoreKiFieldTypeAction(
  shu: ShuOption,
  fields: Option[],
  fieldsOrder?: string[]
) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_KI_FIELD_TYPE,
    payload: { shu, fields, fieldsOrder },
  };
}

/**
 * 指定したソート条件で新台/メイン機種を再取得
 * @param shu 種別データ（種別グループ・種別）
 * @param sort ソートする条件
 * @param order ソート順
 */
function searchDataChainStoreKiSortAction(
  shu: ShuOption,
  sort: string,
  order: OrderType
) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_KI_SORT,
    payload: { shu, sort, order },
  };
}

/**
 * 指定した種別の新台/メイン機種データを破棄する
 * @param shuCode 種別コード
 */
function clearDataChainStoreKiAction(shuCode: string) {
  return {
    type: CLEAR_DATA_CHAIN_STORE_KI,
    payload: { shuCode },
  };
}

/**
 * 新台/メイン機種のデータを全て破棄する
 */
function clearAllDataChainStoreKiAction() {
  return {
    type: CLEAR_ALL_DATA_CHAIN_STORE_KI,
  };
}

/**
 * 新台/メイン機種の並び替え情報を登録する（Sagaで並び替え確定時）
 * @param shuCode 種別コード
 * @param columnsOrder 並び替え情報
 */
function selectDataChainStoreKiColumnsOrderAction(
  shuCode: string,
  columnsOrder: string[]
) {
  return {
    type: SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER,
    payload: { shuCode, columnsOrder },
  };
}

/**
 * 新台/メイン機種の並び替え情報を全て登録する（お気に入り適用時に使用）
 * @param columnsOrders 並び替え情報（全件）
 */
function selectDataChainStoreKiColumnsOrderAllAction(
  columnsOrders: DataChainStoreKiState['columnsOrder']
) {
  return {
    type: SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER_ALL,
    payload: { columnsOrders },
  };
}

/**
 * 新台/メイン機種の列の並び替え実行時にSagaで再計算する
 * @param shuCode 種別コード
 * @param columnsOrder 並び替え情報
 */
export function changeDataChainStoreKiColumnsOrderAction(
  shuCode: string,
  draggedId?: string,
  droppedId?: string
) {
  return {
    type: CHANGE_DATA_CHAIN_STORE_KI_COLUMNS_ORDER,
    payload: { shuCode, draggedId, droppedId },
  };
}

/**
 * DK-SISを有効にする
 * @param shu 種別データ（種別グループ・種別）
 */
function searchDataChainStoreKiDkSisOnAction(shu: ShuOption) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_KI_DKSIS_ON,
    payload: { shu },
  };
}

/**
 * DK-SISを無効にする
 * @param shu 種別データ（種別グループ・種別）
 */
function searchDataChainStoreKiDkSisOffAction(shu: ShuOption) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_KI_DKSIS_OFF,
    payload: { shu },
  };
}

/**
 * 指定したDK-SISの検索条件で再取得する
 * @param shu 種別データ（種別グループ・種別）
 * @param params DK-SIS個別の検索パラメータ
 */
function searchDataChainStoreKiDkSisAction(
  shu: ShuOption,
  params: DkSisSearchParams
) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_KI_DKSIS,
    payload: { shu, params },
  };
}

/**
 * 初期化
 */
function renewDataChainStoreKi() {
  return {
    type: RENEW_DATA_CHAIN_STORE_KI,
  };
}

export const DataChainStoreKiActionCreators = {
  fetchDataChainStoreKiAction,
  fetchDataChainStoreKiRequestAction,
  fetchDataChainStoreKiSuccessAction,
  initDataChainStoreKiAction,
  searchDataChainStoreKiAction,
  searchDataChainStoreKiResetAction,
  searchDataChainStoreKiFavoriteAction,
  searchDataChainStoreKiFieldTypeAction,
  searchDataChainStoreKiSortAction,
  clearDataChainStoreKiAction,
  clearAllDataChainStoreKiAction,
  selectDataChainStoreKiColumnsOrderAction,
  selectDataChainStoreKiColumnsOrderAllAction,
  changeDataChainStoreKiColumnsOrderAction,
  searchDataChainStoreKiDkSisOnAction,
  searchDataChainStoreKiDkSisOffAction,
  searchDataChainStoreKiDkSisAction,
  renewDataChainStoreKi,
};

/**
 * Actions
 */

export type FetchDataChainStoreKiAction = ReturnType<
  typeof fetchDataChainStoreKiAction
>;
export type FetchDataChainStoreKiSuccessAction = ReturnType<
  typeof fetchDataChainStoreKiSuccessAction
>;
export type InitDataChainStoreKiAction = ReturnType<
  typeof initDataChainStoreKiAction
>;
export type SearchDataChainStoreKiAction = ReturnType<
  typeof searchDataChainStoreKiAction
>;
type SearchDataChainStoreKiResetAction = ReturnType<
  typeof searchDataChainStoreKiResetAction
>;
export type SearchDataChainStoreKiFieldTypeAction = ReturnType<
  typeof searchDataChainStoreKiFieldTypeAction
>;
export type SearchDataChainStoreKiSortAction = ReturnType<
  typeof searchDataChainStoreKiSortAction
>;
type ClearDataChainStoreKiAction = ReturnType<
  typeof clearDataChainStoreKiAction
>;
type ClearAllDataChainStoreKiAction = ReturnType<
  typeof clearAllDataChainStoreKiAction
>;
type SelectDataChainStoreKiColumnsOrderAction = ReturnType<
  typeof selectDataChainStoreKiColumnsOrderAction
>;
export type ChangeDataChainStoreKiColumnsOrderAction = ReturnType<
  typeof changeDataChainStoreKiColumnsOrderAction
>;
export type SearchDataChainStoreKiDkSisOnAction = ReturnType<
  typeof searchDataChainStoreKiDkSisOnAction
>;
export type SearchDataChainStoreKiDkSisOffAction = ReturnType<
  typeof searchDataChainStoreKiDkSisOffAction
>;
export type SearchDataChainStoreKiDkSisAction = ReturnType<
  typeof searchDataChainStoreKiDkSisAction
>;
export type SearchDataChainStoreKiFavoriteAction = ReturnType<
  typeof searchDataChainStoreKiFavoriteAction
>;

type DataChainStoreKiAction =
  | FetchDataChainStoreKiAction
  | FetchDataChainStoreKiSuccessAction
  | ReturnType<typeof fetchDataChainStoreKiRequestAction>
  | ReturnType<typeof selectDataChainStoreKiColumnsOrderAllAction>
  | InitDataChainStoreKiAction
  | SearchDataChainStoreKiAction
  | SearchDataChainStoreKiResetAction
  | SearchDataChainStoreKiFieldTypeAction
  | SearchDataChainStoreKiSortAction
  | ClearDataChainStoreKiAction
  | ClearAllDataChainStoreKiAction
  | SelectDataChainStoreKiColumnsOrderAction
  | ChangeDataChainStoreKiColumnsOrderAction
  | SearchDataChainStoreKiDkSisOnAction
  | SearchDataChainStoreKiDkSisOffAction
  | SearchDataChainStoreKiDkSisAction
  | SearchDataChainStoreKiFavoriteAction
  | ReturnType<typeof renewDataChainStoreKi>;

/**
 * State
 */

export type DataChainStoreKiState = {
  isLoading: {
    [key: string]: boolean | undefined;
  };
  shus: {
    [key: string]: DataChainStoreKi | undefined;
  };
  columnsOrder: {
    [key: string]: string[] | undefined;
  };
};

// Stateの初期値
const initialState: DataChainStoreKiState = {
  isLoading: {},
  shus: {},
  columnsOrder: {},
};

/**
 * Selector
 */

/**
 * 新台/メイン機種に関するデータ全てを取得する
 * @returns 新台/メイン機種に関するデータ
 */
const dataChainStoreKiSelector = (state: RootState) => state.dataChainStoreKi;

/**
 * 全ての新台/メイン機種データを取得する
 * @returns 全ての新台/メイン機種データ
 */
export const dataChainStoreKiDataSelector = (state: RootState) => {
  return state.dataChainStoreKi.shus;
};

/**
 * チェーン店レポート 新台/メイン機種のローディング状態を全て取得する
 * @returns 全てのローディング状態
 */
const dataChainStoreKiLoadingAllSelector = (state: RootState) =>
  state.dataChainStoreKi.isLoading;

/**
 * チェーン店レポート 新台/メイン機種の全テーブルのローディング状態を全て取得する
 *
 * 一つでもローディング状態のテーブルがあればtrueとなる
 */
export const dataChainStoreKiIsAllLoadingSelector = createSelector(
  dataChainStoreKiLoadingAllSelector,
  (all) => Object.values(all).some((item) => !!item)
);

/**
 * 指定した種別と一致する新台/メイン機種データを取得する
 * @param shu 種別データ（種別グループ・種別）
 * @returns 新台/メイン機種データ
 */
export const dataChainStoreKiDataByShuSelector = (shuCode: string) =>
  createSelector(
    [dataChainStoreKiDataSelector, chainStoreReportsSearchConditionSelector],
    (shus, searchCondition) => {
      const defaultValue: DataChainStoreKi = {
        setting: { ...searchCondition },
        data: {
          columns: [],
          rows: [],
        },
      };

      return shus[shuCode] ?? defaultValue;
    }
  );

/**
 * 並び替え後のテーブルデータを取得する
 * @param shuCode 種別コード
 * @returns 並び替え後のテーブルデータ
 */
export const dataChainStoreKiOrderedData = (shuCode: string) =>
  createSelector(
    [
      dataChainStoreKiDataByShuSelector(shuCode),
      dataChainStoreKiColumnsOrderSelector(shuCode),
    ],
    (tableData, ordered) => {
      if (!ordered) return tableData.data;

      return getOrderedTableData(
        ordered,
        tableData.data.columns,
        tableData.data.rows
      );
    }
  );

/**
 * 指定した種別の新台/メイン機種データが存在するか取得する
 * @param shu 種別データ（種別グループ・種別）
 * @returns 存在するか（存在する場合: true）
 */
export const dataChainStoreKiDataIsExistSelector = (shuCode: string) =>
  createSelector(dataChainStoreKiSelector, (data) => {
    return !!data.shus[shuCode];
  });

/**
 * 指定した種別の新台/メイン機種データの検索条件を取得する
 * @param shu 種別データ（種別グループ・種別）
 * @returns 現在の検索条件
 */
export const dataChainStoreKiDataSearchConditionSelector = (shuCode: string) =>
  createSelector(dataChainStoreKiDataByShuSelector(shuCode), (data) => {
    return data.setting;
  });

/**
 * 指定した種別の新台/メイン機種で選択されている表示項目一覧を取得する
 * @param shu 種別データ（種別グループ・種別）
 * @returns 現在選択されている表示項目一覧
 */
export const dataChainStoreKiSelectedFieldsSelector = (shuCode: string) =>
  createSelector(
    dataChainStoreKiDataSearchConditionSelector(shuCode),
    (param) => param.fields ?? []
  );

/**
 * 指定した種別の新台/メイン機種が現在ローディング状態か取得する
 * @param shu 種別データ（種別グループ・種別）
 * @returns ローディング状態
 */
export const dataChainStoreKiDataIsLoadingSelector = (shuCode: string) =>
  createSelector(dataChainStoreKiSelector, (dataChainStoreKi) => {
    return dataChainStoreKi.isLoading[shuCode] ?? false;
  });

/**
 * 新台/メイン機種テーブルのすべての検索条件を取得する（お気に入り保存用）
 * @returns 現在の新台/メイン機種テーブルのすべての検索条件
 */
export const dataChainStoreKiSettingSelector = createSelector(
  dataChainStoreKiDataSelector,
  (data) => {
    return Object.fromEntries(
      Object.entries(data).map((entry) => {
        const [key, value] = entry;
        return [key, value?.setting];
      })
    );
  }
);

/**
 * 新台/メイン機種のテーブルデータのカラム部分（カラム行）のみ取得
 * @param shuCode 種別コード
 * @returns テーブルデータのカラム行（rowsを除いた部分）
 */
export const dataChainStoreKiDataColumnsSelector = (shuCode: string) =>
  createSelector(dataChainStoreKiSelector, (data) => {
    const dataChainStoreKi = data.shus[shuCode];
    if (dataChainStoreKi == null) {
      return [];
    }
    return dataChainStoreKi.data.columns;
  });

/**
 * 新台/メイン機種の全ての並び替え情報を取得
 * @returns 全ての並び替え情報
 */
export const dataChainStoreKiColumnsOrderDataSelector = (state: RootState) =>
  state.dataChainStoreKi.columnsOrder;

/**
 * 指定した種別の並び替え情報を取得
 * @param shuCode 種別コード
 * @returns 並び替え情報
 */
export const dataChainStoreKiColumnsOrderSelector = (shuCode: string) =>
  createSelector(
    dataChainStoreKiColumnsOrderDataSelector,
    (columnsOrder) => columnsOrder[shuCode]
  );

/**
 * データのある種別グループコードを返す
 */
export const dataChainStoreKiGroupCodesWithDataSelector = createSelector(
  dataChainStoreKiDataSelector,
  (data) =>
    Object.keys(data).filter((x) => {
      const dataByShuCode = data[x];
      if (!dataByShuCode) {
        return false;
      }
      return dataByShuCode.data.rows?.length > 0;
    })
);

/**
 * Reducer
 */

export function dataChainStoreKiReducer(
  state = initialState,
  action: DataChainStoreKiAction
): DataChainStoreKiState {
  switch (action.type) {
    case FETCH_DATA_CHAIN_STORE_KI_REQUEST:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.payload.shuCode]: true,
        },
      };
    case FETCH_DATA_CHAIN_STORE_KI_SUCCESS:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.payload.shuCode]: false,
        },
        shus: {
          ...state.shus,
          [action.payload.shuCode]: action.payload.shu,
        },
      };
    case CLEAR_DATA_CHAIN_STORE_KI:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.payload.shuCode]: false,
        },
        shus: {
          ...state.shus,
          [action.payload.shuCode]: undefined,
        },
      };
    case CLEAR_ALL_DATA_CHAIN_STORE_KI:
      return {
        ...state,
        shus: {},
      };
    case SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER:
      return {
        ...state,
        columnsOrder: {
          ...state.columnsOrder,
          [action.payload.shuCode]: action.payload.columnsOrder,
        },
      };
    case SELECT_DATA_CHAIN_STORE_KI_COLUMNS_ORDER_ALL:
      return {
        ...state,
        columnsOrder: action.payload.columnsOrders,
      };
    case SEARCH_DATA_CHAIN_STORE_KI_RESET: {
      // お気に入りのデフォルトが選択されたとき、列の並び順をデフォルトに戻すため、columnsOrderを空にする
      return {
        ...state,
        columnsOrder: {},
      };
    }
    case RENEW_DATA_CHAIN_STORE_KI:
      return initialState;
    default:
      return state;
  }
}
